import { useState, useEffect } from "react";
import { ShoppingBag, Plus, Trash2, AlertCircle, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { useNavigation } from "@/components/layout/app-layout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useStockCosting } from "@/hooks/use-stock-costing";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface PurchaseLineItem {
  id: string;
  stockItemId: string;
  stockItemName: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
}

interface FormData {
  reference: string;
  purchaseDate: string;
  supplier: string;
  location: string;
  invoiceNumber: string;
  notes: string;
}

interface FormErrors {
  reference?: string;
  purchaseDate?: string;
  supplier?: string;
  location?: string;
  invoiceNumber?: string;
  lineItems?: string;
}

// Mock stock items data
const STOCK_ITEMS = [
  { id: "1", name: "Widget A", unitPrice: 10.00 },
  { id: "2", name: "Widget B", unitPrice: 15.50 },
  { id: "3", name: "Gadget X", unitPrice: 25.00 },
  { id: "4", name: "Gadget Y", unitPrice: 30.75 },
  { id: "5", name: "Component Z", unitPrice: 5.25 },
];

const SUPPLIERS = [
  "Supplier A Ltd",
  "Supplier B Corp",
  "Supplier C Inc",
  "Global Supplies Co",
  "Local Vendor",
];

const LOCATIONS = [
  "Warehouse A",
  "Warehouse B",
  "Retail Store",
  "Distribution Center",
  "Storage Room",
];

export function PurchaseMovementPage() {
  const { navigateTo } = useNavigation();
  const { processPurchaseReceipt, valuationMethod, getValuationMethodDisplayName } = useStockCosting();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  // Form state
  const [formData, setFormData] = useState<FormData>({
    reference: "",
    purchaseDate: new Date().toISOString().split("T")[0],
    supplier: "",
    location: "",
    invoiceNumber: "",
    notes: "",
  });

  // Line items state
  const [lineItems, setLineItems] = useState<PurchaseLineItem[]>([]);

  // Auto-focus on reference field when component mounts
  useEffect(() => {
    const referenceInput = document.getElementById("reference");
    if (referenceInput) {
      referenceInput.focus();
    }
  }, []);

  // Track unsaved changes
  useEffect(() => {
    const hasFormData =
      formData.reference ||
      formData.supplier ||
      formData.location ||
      formData.invoiceNumber ||
      formData.notes;
    const hasItems = lineItems.length > 0;
    setHasUnsavedChanges(hasFormData || hasItems);
  }, [formData, lineItems]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);



  const handleInputChange = (
    field: keyof FormData,
    value: string
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const addLineItem = () => {
    const newItem: PurchaseLineItem = {
      id: `temp-${Date.now()}`,
      stockItemId: "",
      stockItemName: "",
      quantity: 0,
      unitPrice: 0,
      lineTotal: 0,
    };
    setLineItems((prev) => [...prev, newItem]);
  };

  const removeLineItem = (id: string) => {
    setLineItems((prev) => prev.filter((item) => item.id !== id));
    // Clear line items error if exists
    if (errors.lineItems) {
      setErrors((prev) => ({ ...prev, lineItems: undefined }));
    }
  };

  const updateLineItem = (
    id: string,
    field: keyof PurchaseLineItem,
    value: string | number
  ) => {
    setLineItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;

        const updated = { ...item, [field]: value };

        // If stock item changed, update name and unit price
        if (field === "stockItemId") {
          const stockItem = STOCK_ITEMS.find((s) => s.id === value);
          if (stockItem) {
            updated.stockItemName = stockItem.name;
            updated.unitPrice = stockItem.unitPrice;
          }
        }

        // Recalculate line total
        updated.lineTotal = updated.quantity * updated.unitPrice;

        return updated;
      })
    );
  };

  const calculateTotals = () => {
    const totalItems = lineItems.length;
    const totalQuantity = lineItems.reduce((sum, item) => sum + item.quantity, 0);
    const totalValue = lineItems.reduce((sum, item) => sum + item.lineTotal, 0);
    return { totalItems, totalQuantity, totalValue };
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Header validation
    if (!formData.reference.trim()) {
      newErrors.reference = "Purchase reference is required";
    }

    if (!formData.purchaseDate) {
      newErrors.purchaseDate = "Purchase date is required";
    } else {
      const selectedDate = new Date(formData.purchaseDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate > today) {
        newErrors.purchaseDate = "Purchase date cannot be in the future";
      }
    }

    if (!formData.supplier) {
      newErrors.supplier = "Supplier is required";
    }

    if (!formData.location) {
      newErrors.location = "Location is required";
    }

    // Line items validation
    if (lineItems.length === 0) {
      newErrors.lineItems = "At least one item is required";
    } else {
      // Check for incomplete line items
      const hasIncompleteItems = lineItems.some(
        (item) => !item.stockItemId || item.quantity <= 0
      );
      if (hasIncompleteItems) {
        newErrors.lineItems =
          "All line items must have a stock item selected and quantity greater than 0";
      }

      // Check for duplicate stock items
      const stockItemIds = lineItems.map((item) => item.stockItemId);
      const hasDuplicates = stockItemIds.some(
        (id, index) => id && stockItemIds.indexOf(id) !== index
      );
      if (hasDuplicates) {
        newErrors.lineItems = "Duplicate stock items are not allowed";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      // Scroll to first error
      const firstErrorElement = document.querySelector('[aria-invalid="true"]');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      console.log(`📦 Processing purchase using ${getValuationMethodDisplayName()}`);

      // Process each line item with FIFO or Average Cost
      const movements = lineItems.map((item) => {
        // Find the stock item from mock data
        const stockItem = STOCK_ITEMS.find(s => s.id === item.stockItemId);
        
        if (stockItem) {
          // Process the purchase using the costing hook (FIFO or Average Cost)
          const costingResult = processPurchaseReceipt(
            {
              code: item.stockItemId,
              description: item.stockItemName,
              quantityOnHand: 0, // Would come from database in real app
              averageCost: stockItem.unitPrice,
            },
            item.quantity,
            item.unitPrice,
            formData.location,
            "", // No specific bin location for purchase movement
            formData.reference
          );

          console.log(`  ✅ Processed: ${item.stockItemName} - Qty: ${item.quantity}, Cost: £${item.unitPrice.toFixed(2)}`);
        }

        return {
          type: "Purchase",
          source: "Manual",
          reference: formData.reference,
          date: formData.purchaseDate,
          stockItemId: item.stockItemId,
          stockItemName: item.stockItemName,
          location: formData.location,
          quantity: item.quantity, // Positive quantity for purchase
          unitPrice: item.unitPrice,
          totalValue: item.lineTotal,
          supplier: formData.supplier,
          invoiceNumber: formData.invoiceNumber,
          notes: formData.notes,
        };
      });

      console.log("Purchase movements created:", movements);

      // Show success message
      const totals = calculateTotals();
      alert(
        `Purchase recorded successfully!\n\n` +
        `${totals.totalItems} item(s) added to ${formData.location}\n` +
        `Total Quantity: ${totals.totalQuantity} units\n` +
        `Supplier: ${formData.supplier}`
      );

      // Clear unsaved changes flag
      setHasUnsavedChanges(false);

      // Navigate to stock movements history
      navigateTo("stock-movements");
    } catch (error) {
      console.error("Error saving purchase:", error);
      alert("Failed to save purchase. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      const confirmCancel = window.confirm(
        "You have unsaved changes. Are you sure you want to cancel?"
      );
      if (!confirmCancel) return;
    }
    navigateTo("new-stock-movement");
  };

  const totals = calculateTotals();

  return (
    <div className="space-y-6 pb-20">
      {/* Page Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <ShoppingBag className="h-8 w-8 text-primary" />
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">
              Record Purchase
            </h1>
            <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
              🟡 Demo Mode
            </Badge>
            <Badge 
              variant="outline" 
              className={valuationMethod === 'FIFO' 
                ? "bg-purple-50 text-purple-700 border-purple-200" 
                : "bg-green-50 text-green-700 border-green-200"}
            >
              {getValuationMethodDisplayName()}
            </Badge>
          </div>
        </div>
        <p className="text-muted-foreground">
          Record stock received from suppliers
        </p>
        <div className="inline-flex">
          <span className="access-badge access-success">Purchase</span>
        </div>
      </div>

      {/* Valuation Method Info Alert */}
      {valuationMethod === 'fifo' && (
        <Alert className="bg-purple-50 border-purple-200">
          <Info className="h-4 w-4 text-purple-600" />
          <AlertDescription className="text-purple-900">
            <strong>FIFO Mode Active:</strong> Each purchase creates a new cost layer. 
            When items are sold, costs are consumed from the oldest layers first.
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Section 1: Purchase Header */}
        <Card>
          <CardHeader>
            <CardTitle>Purchase Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Purchase Reference */}
              <div className="space-y-2">
                <Label htmlFor="reference">
                  Purchase Reference <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="reference"
                  placeholder="e.g., PO-2024-001"
                  value={formData.reference}
                  onChange={(e) => handleInputChange("reference", e.target.value)}
                  aria-invalid={!!errors.reference}
                  aria-describedby={
                    errors.reference ? "reference-error" : undefined
                  }
                />
                {errors.reference && (
                  <p
                    id="reference-error"
                    className="text-sm text-destructive flex items-center gap-1"
                  >
                    <AlertCircle className="h-4 w-4" />
                    {errors.reference}
                  </p>
                )}
              </div>

              {/* Purchase Date */}
              <div className="space-y-2">
                <Label htmlFor="purchaseDate">
                  Purchase Date <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="purchaseDate"
                  type="date"
                  value={formData.purchaseDate}
                  onChange={(e) => handleInputChange("purchaseDate", e.target.value)}
                  aria-invalid={!!errors.purchaseDate}
                  aria-describedby={
                    errors.purchaseDate ? "purchaseDate-error" : undefined
                  }
                />
                {errors.purchaseDate && (
                  <p
                    id="purchaseDate-error"
                    className="text-sm text-destructive flex items-center gap-1"
                  >
                    <AlertCircle className="h-4 w-4" />
                    {errors.purchaseDate}
                  </p>
                )}
              </div>

              {/* Supplier */}
              <div className="space-y-2">
                <Label htmlFor="supplier">
                  Supplier <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.supplier}
                  onValueChange={(value) => handleInputChange("supplier", value)}
                >
                  <SelectTrigger
                    id="supplier"
                    aria-invalid={!!errors.supplier}
                    aria-describedby={errors.supplier ? "supplier-error" : undefined}
                  >
                    <SelectValue placeholder="Select supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    {SUPPLIERS.map((supplier) => (
                      <SelectItem key={supplier} value={supplier}>
                        {supplier}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.supplier && (
                  <p
                    id="supplier-error"
                    className="text-sm text-destructive flex items-center gap-1"
                  >
                    <AlertCircle className="h-4 w-4" />
                    {errors.supplier}
                  </p>
                )}
                <p className="text-sm text-muted-foreground">
                  Supplier providing the stock
                </p>
              </div>

              {/* Location */}
              <div className="space-y-2">
                <Label htmlFor="location">
                  Receiving Location <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.location}
                  onValueChange={(value) => handleInputChange("location", value)}
                >
                  <SelectTrigger
                    id="location"
                    aria-invalid={!!errors.location}
                    aria-describedby={errors.location ? "location-error" : undefined}
                  >
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCATIONS.map((location) => (
                      <SelectItem key={location} value={location}>
                        {location}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.location && (
                  <p
                    id="location-error"
                    className="text-sm text-destructive flex items-center gap-1"
                  >
                    <AlertCircle className="h-4 w-4" />
                    {errors.location}
                  </p>
                )}
                <p className="text-sm text-muted-foreground">
                  Where stock will be received
                </p>
              </div>

              {/* Invoice Number */}
              <div className="space-y-2">
                <Label htmlFor="invoiceNumber">Supplier Invoice Number</Label>
                <Input
                  id="invoiceNumber"
                  placeholder="e.g., INV-12345"
                  value={formData.invoiceNumber}
                  onChange={(e) =>
                    handleInputChange("invoiceNumber", e.target.value)
                  }
                />
                <p className="text-sm text-muted-foreground">
                  Optional reference to supplier invoice
                </p>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Purchase Notes</Label>
              <Textarea
                id="notes"
                placeholder="Additional details about this purchase..."
                value={formData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Items to Purchase */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Items to Purchase</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Add items being received from supplier
                </p>
              </div>
              <Button
                type="button"
                onClick={addLineItem}
                size="sm"
                disabled={lineItems.length >= 50}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {errors.lineItems && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{errors.lineItems}</AlertDescription>
              </Alert>
            )}

            {lineItems.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed rounded-lg">
                <ShoppingBag className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">
                  No items added yet
                </p>
                <Button type="button" onClick={addLineItem} variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Item
                </Button>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[300px]">Stock Item</TableHead>
                        <TableHead className="w-[150px]">Quantity</TableHead>
                        <TableHead className="w-[150px]">Unit Price</TableHead>
                        <TableHead className="w-[150px]">Line Total</TableHead>
                        <TableHead className="w-[80px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lineItems.map((item, index) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <Select
                              value={item.stockItemId}
                              onValueChange={(value) =>
                                updateLineItem(item.id, "stockItemId", value)
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select item" />
                              </SelectTrigger>
                              <SelectContent>
                                {STOCK_ITEMS.filter(
                                  (stockItem) =>
                                    !lineItems.some(
                                      (li) =>
                                        li.stockItemId === stockItem.id &&
                                        li.id !== item.id
                                    )
                                ).map((stockItem) => (
                                  <SelectItem
                                    key={stockItem.id}
                                    value={stockItem.id}
                                  >
                                    {stockItem.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min="1"
                              step="1"
                              value={item.quantity || ""}
                              onChange={(e) =>
                                updateLineItem(
                                  item.id,
                                  "quantity",
                                  parseInt(e.target.value) || 0
                                )
                              }
                              placeholder="0"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.unitPrice || ""}
                              onChange={(e) =>
                                updateLineItem(
                                  item.id,
                                  "unitPrice",
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              placeholder="0.00"
                            />
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">
                              £{item.lineTotal.toFixed(2)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeLineItem(item.id)}
                              aria-label={`Remove ${item.stockItemName || "item"}`}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {lineItems.length > 0 && lineItems.length < 50 && (
              <div className="mt-4">
                <Button
                  type="button"
                  onClick={addLineItem}
                  variant="outline"
                  size="sm"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Another Item
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 3: Purchase Summary */}
        {lineItems.length > 0 && (
          <Card className="access-card sticky top-4 z-10">
            <CardHeader>
              <CardTitle>Purchase Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Supplier:</span>
                  <span className="font-medium">
                    {formData.supplier || "Not selected"}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Receiving Location:</span>
                  <span className="font-medium">
                    {formData.location || "Not selected"}
                  </span>
                </div>
                <div className="border-t pt-3 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Total Items:</span>
                    <span className="font-medium">{totals.totalItems}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Total Quantity:</span>
                    <span className="font-medium">{totals.totalQuantity} units</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </form>

      {/* Sticky Footer with Actions */}
      <div className="fixed bottom-0 left-0 right-0 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-20">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting || lineItems.length === 0}
            >
              {isSubmitting ? "Saving..." : "Save Purchase"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
